import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
  Paper,
  Slide,
  Divider,
  Tab,
  Tabs,
  useMediaQuery
} from "@mui/material";
import {
  Add as PlusIcon,
  Edit as EditIcon,
  Delete as TrashIcon,
  Business as TheaterIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  CheckCircle as SuccessIcon,
  Cancel as ErrorIcon,
  Cancel as CancelIcon,
  PersonalVideo as ScreenIcon,
  ChairOutlined as ChairIcon,
  VerifiedOutlined as VerifiedIcon,
  VerifiedUser as TrustIcon,
  Wifi as WifiIcon,
  Fastfood as FoodIcon,
  LocalParking as ParkingIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import api from "../../services/api";

import ModernLoader from "../../components/common/ModernLoader";
import SeatLayoutBuilder from "../../components/admin/SeatLayoutBuilder";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminTheaters = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [theaters, setTheaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSeatLayoutDialog, setOpenSeatLayoutDialog] = useState(false);
  const [editingTheater, setEditingTheater] = useState(null);
  const [selectedTheater, setSelectedTheater] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    address: { city: "", state: "" },
    contactInfo: { phone: "", email: "" },
    amenities: "",
    status: { isActive: true, isVerified: false },
  });

  const [openScreenDialog, setOpenScreenDialog] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [screenFormData, setScreenFormData] = useState({
    name: "",
    screenType: "Standard 2D",
    capacity: 100,
    soundSystem: "Dolby 7.1",
    projectionType: "4K Digital"
  });

  const fetchTheaters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/theaters?limit=1000&isActive=all");
      let data = Array.isArray(response.data) ? response.data : (response.data.theaters || []);
      setTheaters(data.filter(t => t && t._id));
    } catch (error) {
      toast.error("Telemetry sync failure");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTheaters();
    
    // Real-time synchronization
    const handleSync = () => fetchTheaters();
    window.addEventListener("theaters-updated", handleSync);
    window.addEventListener("theater-added", handleSync);
    window.addEventListener("theater-updated", handleSync);
    window.addEventListener("theater-deleted", handleSync);
    window.addEventListener("seat-layout-updated", handleSync);
    
    return () => {
      window.removeEventListener("theaters-updated", handleSync);
      window.removeEventListener("theater-added", handleSync);
      window.removeEventListener("theater-updated", handleSync);
      window.removeEventListener("theater-deleted", handleSync);
      window.removeEventListener("seat-layout-updated", handleSync);
    };
  }, [fetchTheaters]);

  const handleViewAnalytics = async (theater) => {
    try {
      setSelectedTheater(theater);
      setAnalyticsData(null);
      setAnalyticsDialog(true);
      const res = await api.get(`/analytics/theater/${theater._id}`);
      setAnalyticsData(res.data);
    } catch (err) {
      toast.error("Failed to sync metrics");
    }
  };

  const handleOpenDialog = (theater = null) => {
    if (theater) {
      setEditingTheater(theater);
      setFormData({
        name: theater.name || "",
        location: theater.location || "",
        address: {
          city: theater.address?.city || theater.city || "",
          state: theater.address?.state || "",
        },
        contactInfo: {
          phone: theater.contactInfo?.phone || "",
          email: theater.contactInfo?.email || "",
        },
        amenities: Array.isArray(theater.amenities) ? theater.amenities.join(", ") : theater.amenities || "",
        status: {
          isActive: theater.status?.isActive !== false,
          isVerified: theater.status?.isVerified || false,
        },
      });
    } else {
      setEditingTheater(null);
      setFormData({
        name: "",
        location: "",
        address: { city: "", state: "" },
        contactInfo: { phone: "", email: "" },
        amenities: "",
        status: { isActive: true, isVerified: false },
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTheater(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        amenities: formData.amenities.split(",").map(a => a.trim()).filter(a => a),
      };

      if (editingTheater) {
        await api.put(`/theaters/${editingTheater._id}`, submitData);
        toast.success("Intelligence updated");
      } else {
        await api.post("/theaters", submitData);
        toast.success("New Hub registered");
      }
      handleCloseDialog();
      fetchTheaters();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (theater, type) => {
    try {
      const payload = type === 'active' 
        ? { isActive: !theater.status?.isActive }
        : { isVerified: !theater.status?.isVerified };
        
      await api.patch(`/theaters/${theater._id}/status`, payload);
      toast.success(`${type === 'active' ? 'Status' : 'Verification'} updated`);
      fetchTheaters();
    } catch (err) {
      toast.error("Status update failure");
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Decommission Hub "${name}"?`)) {
      try {
        await api.delete(`/theaters/${id}`);
        toast.success("Hub decommissioned");
        fetchTheaters();
      } catch (error) {
        toast.error("Purge failure");
      }
    }
  };

  const handleOpenScreenDialog = (theater, screen = null) => {
    setSelectedTheater(theater);
    if (screen) {
      setEditingScreen(screen);
      setScreenFormData({
        name: screen.name || "",
        screenType: screen.screenType || "Standard 2D",
        capacity: screen.capacity || 100,
        soundSystem: screen.soundSystem || "Dolby 7.1",
        projectionType: screen.projectionType || "4K Digital"
      });
    } else {
      setEditingScreen(null);
      setScreenFormData({
        name: "",
        screenType: "Standard 2D",
        capacity: 100,
        soundSystem: "Dolby 7.1",
        projectionType: "4K Digital"
      });
    }
    setOpenScreenDialog(true);
  };

  const handleScreenSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (editingScreen) {
        await api.put(`/theaters/${selectedTheater._id}/screens/${editingScreen._id}`, screenFormData);
        toast.success("Auditorium configuration synchronized");
      } else {
        await api.post(`/theaters/${selectedTheater._id}/screens`, screenFormData);
        toast.success("New Auditorium station established");
      }
      setOpenScreenDialog(false);
      fetchTheaters();
    } catch (error) {
      toast.error(error.response?.data?.message || "Screen deployment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScreenDelete = async (theaterId, screenId) => {
    if (window.confirm("Permanently decommission this auditorium station?")) {
      try {
        await api.delete(`/theaters/${theaterId}/screens/${screenId}`);
        toast.success("Station decommissioned");
        fetchTheaters();
      } catch (error) {
        toast.error("Process failure");
      }
    }
  };

  const handleEditSeatLayout = (theater, screen) => {
    navigate(`/admin/theaters/${theater._id}/screens/${screen._id}/layout`);
  };

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Theatrical Project",
      Cell: ({ row, cell }) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), color: "primary.main", borderRadius: "14px" }}>
            <TheaterIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 850 }}>{cell.getValue()}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{row.original.location}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      id: "partner_info",
      accessorFn: (row) => row.partnerId?.userName || "Direct Hub",
      header: "Strategic Partner",
      Cell: ({ row }) => {
        const p = row.original.partnerId;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {p?.userName || row.original.contactInfo?.email?.split('@')[0] || "Direct Hub"}
            </Typography>
            {p?.businessDetails?.legalEntityName && (
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, display: 'block' }}>
                {p.businessDetails.legalEntityName}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: '0.65rem' }}>
              {p?.userEmail || row.original.contactInfo?.email || "internal@system"}
            </Typography>
          </Box>
        );
      }
    },
    {
      accessorKey: "city",
      header: "Registry",
      accessorFn: (row) => row.address?.city || row.city || "Global",
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontWeight: 650 }}>{cell.getValue()}</Typography>
      )
    },
    {
      accessorKey: "screens.length",
      header: "Auditoriums",
      Cell: ({ cell }) => (
        <Chip
          label={`${cell.getValue() || 0} STATIONS`}
          size="small"
          sx={{ fontWeight: 950, fontSize: "0.6rem", bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: "8px" }}
        />
      ),
    },
    {
      accessorKey: "status.isActive",
      header: "Operational",
      Cell: ({ row, cell }) => (
        <FormControlLabel
          control={
            <Switch 
              size="small"
              checked={!!cell.getValue()} 
              onChange={() => handleToggleStatus(row.original, 'active')}
            />
          }
          label={<Typography variant="caption" sx={{ fontWeight: 800 }}>{cell.getValue() ? "LIVE" : "OFF"}</Typography>}
        />
      )
    },
    {
      accessorKey: "status.isVerified",
      header: "Credential",
      Cell: ({ row, cell }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {cell.getValue() ? (
            <Chip icon={<VerifiedIcon sx={{ fontSize: "14px !important" }} />} label="VERIFIED" size="small" color="primary" variant="outlined" sx={{ fontWeight: 900, fontSize: "0.6rem" }} />
          ) : (
            <Chip label="PENDING" size="small" color="warning" variant="outlined" sx={{ fontWeight: 900, fontSize: "0.6rem" }} />
          )}
          <IconButton size="small" onClick={() => handleToggleStatus(row.original, 'verify')}>
            <SuccessIcon sx={{ fontSize: 16, color: cell.getValue() ? "success.main" : "text.disabled" }} />
          </IconButton>
        </Stack>
      )
    },
    {
      id: "actions",
      header: "Control Hub",
      size: 100,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleOpenDialog(row.original)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.original._id, row.original.name)} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: "error.main" }}><TrashIcon sx={{ fontSize: 16 }} /></IconButton>
        </Stack>
      ),
    },
  ], [theme.palette]);

  const table = useMaterialReactTable({
    columns,
    data: theaters,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "30px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.primary.main, 0.05), py: 2.5, fontWeight: 900, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.1em", color: theme.palette.text.secondary } }
  });

  if (loading) return <ModernLoader text="ESTABLISHING HUB COMMUNICATIONS" />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ width: "100%", pb: 4 }}>

        {/* Core Page Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          mb={{ xs: 3, md: 5 }}
          gap={{ xs: 2.5, md: 3 }}
        >
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
              <Box sx={{ p: 1, borderRadius: "12px", bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", flexShrink: 0 }}>
                <TheaterIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
              </Box>
              Network Logistics
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <TrustIcon sx={{ fontSize: 16, color: "success.main" }} /> AUTHORIZED HUB PARTNERS & AUDITORIUM NODES
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            sx={{
              py: 1.8,
              px: 4.5,
              borderRadius: "16px",
              fontWeight: 900,
              background: theme.palette.primary.main,
              boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
              textTransform: "none",
              "&:hover": { background: alpha(theme.palette.primary.main, 0.9), transform: "translateY(-2px)" },
              transition: "all 0.2s"
            }}
          >
            Initialize Hub
          </Button>
        </Stack>

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 5, "& .MuiTabs-indicator": { height: 3, borderRadius: 3 } }}>
          <Tab label={<Typography variant="body2" sx={{ fontWeight: 900 }}>HUB REGISTRY</Typography>} />
          <Tab label={<Typography variant="body2" sx={{ fontWeight: 900 }}>AUDITORIUM CONTROL</Typography>} />
        </Tabs>

        {activeTab === 0 ? (
          <MaterialReactTable table={table} />
        ) : (
          <Grid container spacing={4}>
            {theaters.map((theater) => (
              <Grid size={{ xs: 12 }} key={theater._id}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={4} gap={2}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        {theater.name}
                        <Chip label={`ID: ${theater._id.slice(-6).toUpperCase()}`} size="small" variant="outlined" sx={{ fontWeight: 900, fontSize: "0.6rem" }} />
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 650, mt: 0.5 }}>{theater.location} • {theater.screens?.length || 0} Registered Stations</Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} width={isMobile ? '100%' : 'auto'}>
                      <Button variant="outlined" onClick={() => handleViewAnalytics(theater)} sx={{ borderRadius: "12px", px: 3, fontWeight: 900, flex: 1, borderColor: theme.palette.divider }}>Intelligence</Button>
                      <Button variant="contained" startIcon={<PlusIcon />} onClick={() => handleOpenScreenDialog(theater)} sx={{ borderRadius: "12px", px: 3, fontWeight: 900, flex: 1 }}>Add Station</Button>
                    </Stack>
                  </Stack>
                  
                  <MaterialReactTable
                    columns={[
                      { accessorKey: "name", header: "Station Name", Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 850 }}>{cell.getValue()}</Typography> },
                      { accessorKey: "screenType", header: "Format", Cell: ({ cell }) => <Chip label={cell.getValue()} size="small" sx={{ fontWeight: 900, fontSize: "0.6rem", bgcolor: alpha(theme.palette.primary.main, 0.05), color: "primary.main" }} /> },
                      { accessorKey: "capacity", header: "Max Capacity", Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 900, color: "secondary.main" }}>{cell.getValue()} SEATS</Typography> },
                      { accessorKey: "soundSystem", header: "Audio Engine", Cell: ({ cell }) => <Typography variant="caption" sx={{ fontWeight: 800 }}>{cell.getValue()}</Typography> },
                      {
                        id: "actions",
                        header: "Dynamics",
                        Cell: ({ row }) => (
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Configure Layout"><IconButton size="small" onClick={() => handleEditSeatLayout(theater, row.original)} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}><ChairIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            <Tooltip title="Edit Metadata"><IconButton size="small" onClick={() => handleOpenScreenDialog(theater, row.original)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            <Tooltip title="Decommission"><IconButton size="small" onClick={() => handleScreenDelete(theater._id, row.original._id)} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: "error.main" }}><TrashIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                          </Stack>
                        )
                      }
                    ]}
                    data={theater.screens || []}
                    enableColumnActions={false}
                    enableColumnFilters={true}
                    enablePagination={true}
                    enableSorting={true}
                    enableBottomToolbar={true}
                    enableTopToolbar={true}
                    muiTablePaperProps={{ elevation: 0, sx: { borderRadius: "16px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden" } }}
                    muiTableHeadCellProps={{ sx: { bgcolor: alpha(theme.palette.background.default, 0.5), py: 1.5, fontWeight: 900, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" } }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : "28px",
              backgroundImage: "none",
              bgcolor: theme.palette.background.paper,
              boxShadow: theme.palette.mode === 'dark' ? "0 25px 80px -12px rgba(0, 0, 0, 0.7)" : "0 25px 50px -12px rgba(19, 19, 22, 0.25)",
              maxHeight: "92vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }
          }}
        >
          <form onSubmit={handleSubmit} style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box sx={{
              p: { xs: 3, md: 4 },
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: alpha(theme.palette.background.default, 0.5),
              flexShrink: 0
            }}>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box sx={{
                  p: 1.5,
                  borderRadius: "16px",
                  bgcolor: "primary.main",
                  color: theme.palette.primary.contrastText,
                  display: "flex",
                  boxShadow: "0 8px 16px -4px " + alpha(theme.palette.primary.main, 0.5)
                }}>
                  <TheaterIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.text.primary, letterSpacing: "-0.02em" }}>
                    {editingTheater ? "Refine Hub Logistics" : "Authorize New Hub"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    {editingTheater ? `Operational ID: ${editingTheater._id}` : "Awaiting Deployment"}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleCloseDialog} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03), "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) } }}>
                <CancelIcon />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: { xs: 3, md: 5 }, bgcolor: theme.palette.background.paper, flexGrow: 1, overflowY: "auto" }}>
              <Grid container spacing={4}>
                {/* Registry Details */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="overline" sx={{ fontWeight: 900, color: "primary.main", letterSpacing: "0.15em" }}>HUB REGISTRY</Typography>
                  <TextField
                    fullWidth
                    placeholder="Official Theater Network Name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    variant="outlined"
                    sx={{ mt: 1.5 }}
                    InputProps={{ sx: { borderRadius: "16px", fontWeight: 800, fontSize: "1.2rem", bgcolor: alpha(theme.palette.background.default, 0.4) } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>GEOGRAPHIC LOCATION</Typography>
                  <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Metro/City"
                        value={formData.address.city}
                        onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })}
                        variant="outlined"
                        required
                        InputProps={{ sx: { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3) } }}
                      />
                      <TextField
                        fullWidth
                        label="Full Operational Address"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        variant="outlined"
                        multiline
                        rows={3}
                        required
                        InputProps={{ sx: { borderRadius: "18px", bgcolor: alpha(theme.palette.background.default, 0.3), lineHeight: 1.6 } }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>COMMUNICATION NODES</Typography>
                  <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), height: "calc(100% - 24px)" }}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Secure Hotline"
                        value={formData.contactInfo.phone}
                        onChange={(e) => setFormData({ ...formData, contactInfo: { ...formData.contactInfo, phone: e.target.value } })}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <PhoneIcon sx={{ mr: 1, color: "primary.main", fontSize: 18 }} />,
                          sx: { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3), fontWeight: 800 }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Administration Email"
                        value={formData.contactInfo.email}
                        onChange={(e) => setFormData({ ...formData, contactInfo: { ...formData.contactInfo, email: e.target.value } })}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ mr: 1, color: "secondary.main", fontSize: 18 }} />,
                          sx: { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3), fontWeight: 800 }
                        }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>PREMIUM INFRASTRUCTURE & AMENITIES</Typography>
                  <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="IMAX, 4DX, Laser Projection, Premium Dining..."
                      value={formData.amenities}
                      onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: "20px", fontWeight: 500, p: 2.5 } }}
                    />
                    <Stack direction="row" spacing={2} flexWrap="wrap" mt={3} gap={1.5}>
                      {[
                        { icon: <WifiIcon />, label: "Fiber Tech" },
                        { icon: <ParkingIcon />, label: "Secured Valet" },
                        { icon: <FoodIcon />, label: "Dine-In" },
                        { icon: <ScreenIcon />, label: "4K Laser" }
                      ].map((item, idx) => (
                        <Chip key={idx} icon={item.icon} label={item.label} variant="outlined" sx={{ fontWeight: 800, px: 2, py: 2.5, borderRadius: "12px", "& .MuiChip-icon": { color: "primary.main" } }} />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${theme.palette.divider}` }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={5} justifyContent="center">
                      <FormControlLabel
                        control={<Switch checked={formData.status.isActive} onChange={(e) => setFormData({ ...formData, status: { ...formData.status, isActive: e.target.checked } })} color="success" />}
                        label={<Typography sx={{ fontWeight: 850, fontSize: "0.85rem", color: theme.palette.text.primary }}>LIVE DEPLOYMENT</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={formData.status.isVerified} onChange={(e) => setFormData({ ...formData, status: { ...formData.status, isVerified: e.target.checked } })} color="primary" />}
                        label={<Typography sx={{ fontWeight: 850, fontSize: "0.85rem", color: theme.palette.text.primary }}>VERIFIED PARTNER</Typography>}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>

            <Box sx={{
              p: { xs: 3, md: 4 },
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              flexShrink: 0
            }}>
              <Button onClick={handleCloseDialog} sx={{ fontWeight: 800, color: "text.secondary", px: 4, borderRadius: "14px", textTransform: "none" }}>Dismiss</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{
                  borderRadius: "16px",
                  px: 6,
                  py: 1.8,
                  fontWeight: 900,
                  textTransform: "none",
                  boxShadow: "0 12px 24px -6px " + alpha(theme.palette.primary.main, 0.4),
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 32px -8px " + alpha(theme.palette.primary.main, 0.5) }
                }}
              >
                {submitting ? "Processing..." : (editingTheater ? "Finalize Registry" : "Initialize Hub")}
              </Button>
            </Box>
          </form>
        </Dialog>



        {/* Screen Management Dialog */}
        <Dialog
          open={openScreenDialog}
          onClose={() => setOpenScreenDialog(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "24px", p: 2 } }}
        >
          <form onSubmit={handleScreenSubmit}>
            <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: "10px", bgcolor: "secondary.main", color: "white", display: "flex" }}>
                <ScreenIcon sx={{ fontSize: 20 }} />
              </Box>
              {editingScreen ? "Refine Auditorium Metadata" : "Add New Station Node"}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField 
                  fullWidth 
                  label="Station Identification (Name)" 
                  value={screenFormData.name}
                  onChange={(e) => setScreenFormData({ ...screenFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Audi 01, IMAX Prime"
                  InputProps={{ sx: { borderRadius: "12px", fontWeight: 700 } }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Visual Format"
                      value={screenFormData.screenType}
                      onChange={(e) => setScreenFormData({ ...screenFormData, screenType: e.target.value })}
                      InputProps={{ sx: { borderRadius: "12px" } }}
                    >
                      {["Standard 2D", "RealD 3D", "IMAX with Laser", "4DX", "Dolby Cinema", "ICE Immersive"].map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Design Capacity"
                      value={screenFormData.capacity}
                      onChange={(e) => setScreenFormData({ ...screenFormData, capacity: parseInt(e.target.value) })}
                      InputProps={{ sx: { borderRadius: "12px" } }}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Audio Engine"
                  value={screenFormData.soundSystem}
                  onChange={(e) => setScreenFormData({ ...screenFormData, soundSystem: e.target.value })}
                  placeholder="e.g., Dolby Atmos, 7.1 Surround"
                  InputProps={{ sx: { borderRadius: "12px" } }}
                />
                <TextField
                  fullWidth
                  label="Projection Architecture"
                  value={screenFormData.projectionType}
                  onChange={(e) => setScreenFormData({ ...screenFormData, projectionType: e.target.value })}
                  placeholder="e.g., 4K Dual Laser, Digital"
                  InputProps={{ sx: { borderRadius: "12px" } }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1.5 }}>
              <Button onClick={() => setOpenScreenDialog(false)} sx={{ fontWeight: 800, color: "text.secondary" }}>Abort</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={submitting} 
                sx={{ borderRadius: "12px", px: 4, fontWeight: 900 }}
              >
                {submitting ? "Processing..." : (editingScreen ? "Finalize" : "Deploy Station")}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Analytics Dialog */}
        <Dialog open={analyticsDialog} onClose={() => setAnalyticsDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "24px", p: 2 } }}>
          <DialogTitle sx={{ fontWeight: 900, borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: "primary.main", letterSpacing: "0.1em" }}>HUB INTELLIGENCE</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedTheater?.name}</Typography>
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            {!analyticsData ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.5 }}>Retrieving telemetry...</Typography>
              </Box>
            ) : (
              <Stack spacing={3}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", bgcolor: alpha(theme.palette.success.main, 0.1), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "success.main" }}>TOTAL HUB REVENUE (30D)</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: "success.main", mt: 0.5 }}>₹{analyticsData.revenue.toLocaleString()}</Typography>
                </Paper>
                
                <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "primary.main" }}>ADMIN COMMISSION CAPTURED</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: "primary.main", mt: 0.5 }}>₹{analyticsData.adminRevenue.toLocaleString()}</Typography>
                </Paper>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{analyticsData.totalBookings}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>BOOKINGS</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{analyticsData.totalTicketsSold}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>TICKETS SOLD</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setAnalyticsDialog(false)} sx={{ fontWeight: 800, borderRadius: "12px", px: 4 }}>Dismiss</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AdminTheaters;

